// SRS — 5-box Leitner spaced repetition.
//
// Decision (Session 19G §5.2):
//   - Box intervals (days until next review):
//       1 → 0   (new / failed; due immediately, re-shown same session)
//       2 → 1
//       3 → 3
//       4 → 7
//       5 → 30  (retired; deep memory)
//   - Rating semantics on review:
//       1 (Again): box → 1, nextDue = now (re-show this session)
//       2 (Hard) : box stays, nextDue = now + half-interval
//       3 (Good) : box → min(box+1, 5), nextDue = now + full-interval-of-new-box
//       4 (Easy) : box → min(box+2, 5), nextDue = now + full-interval-of-new-box
//   - Cards never reviewed (no entry in srs[]) are treated as box 1 with nextDue=now.
//
// Why these intervals (not Anki SM-2): we don't have a forgetting-curve adapter
// (no detailed grade history per card), and 主母's exam is 4 days out — the
// interval table needs to fit a short horizon. 0/1/3/7/30 is pedagogically
// reasonable + simple to reason about.

import type { Flashcard } from '@examforge/shared/schemas';
import type { CourseProgress, SrsEntry } from '@examforge/shared/schemas';

export type SrsRating = 1 | 2 | 3 | 4;

export const BOX_INTERVAL_DAYS: Record<number, number> = {
  1: 0,
  2: 1,
  3: 3,
  4: 7,
  5: 30,
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/** Days a card stays in its current box before becoming due. */
export function intervalDays(box: number): number {
  return BOX_INTERVAL_DAYS[box] ?? 0;
}

/** Get the SRS entry for a card, synthesizing a "never seen" placeholder. */
export function entryFor(progress: CourseProgress, cardId: string, now: Date = new Date()): SrsEntry {
  const existing = progress.srs[cardId];
  if (existing) return existing;
  const iso = now.toISOString();
  return { box: 1, lastSeen: iso, nextDue: iso };
}

export function isDue(entry: SrsEntry, now: Date = new Date()): boolean {
  return new Date(entry.nextDue).getTime() <= now.getTime();
}

/** Cards due for review now, optionally filtered to a specific case. */
export function getDueCards(
  flashcards: readonly Flashcard[],
  progress: CourseProgress,
  opts: { caseId?: string | null; now?: Date } = {},
): Flashcard[] {
  const now = opts.now ?? new Date();
  return flashcards.filter((fc) => {
    if (opts.caseId !== undefined && opts.caseId !== null && fc.caseId !== opts.caseId) return false;
    return isDue(entryFor(progress, fc.id, now), now);
  });
}

/** Apply a rating to a card; returns the new SRS entry. Pure. */
export function rateCard(
  prev: SrsEntry,
  rating: SrsRating,
  now: Date = new Date(),
): SrsEntry {
  let nextBox: number;
  let intervalMs: number;
  switch (rating) {
    case 1: // Again — bury back at box 1, due now (re-show same session).
      nextBox = 1;
      intervalMs = 0;
      break;
    case 2: // Hard — same box, half-interval.
      nextBox = prev.box;
      intervalMs = (intervalDays(nextBox) / 2) * MS_PER_DAY;
      break;
    case 3: // Good — promote one box.
      nextBox = Math.min(prev.box + 1, 5);
      intervalMs = intervalDays(nextBox) * MS_PER_DAY;
      break;
    case 4: // Easy — promote two boxes.
      nextBox = Math.min(prev.box + 2, 5);
      intervalMs = intervalDays(nextBox) * MS_PER_DAY;
      break;
  }
  return {
    box: nextBox,
    lastSeen: now.toISOString(),
    nextDue: new Date(now.getTime() + intervalMs).toISOString(),
  };
}

/** Box → readable label (en/zh). */
export const BOX_LABELS: Record<number, { en: string; zh: string }> = {
  1: { en: 'New', zh: '新卡' },
  2: { en: 'Learning', zh: '初学' },
  3: { en: 'Familiar', zh: '熟悉' },
  4: { en: 'Strong', zh: '巩固' },
  5: { en: 'Retired', zh: '已掌握' },
};
